import { remoteApiRequest } from '@/data/api/remote-api'
import { getActiveIdentity } from '@/services/identity/local-identity'
import type { MindLayoutMode, MindLink, MindTreeNode } from '@/components/canvas/mind-utils'

export const CANVAS_MODULES = ['资料分析', '判断推理', '言语理解', '数量关系', '申论', '常识判断', '复盘整理', '错题记录'] as const

export interface CanvasCloudSnapshot {
  v: 2
  savedAt: number
  writerDeviceId?: string
  hierarchyRevision?: string
  tree: MindTreeNode[]
  links: MindLink[]
  mode: MindLayoutMode
  hidden: string[]
}

interface PendingCanvasChange {
  id: string
  changedAt: number
}

interface CanvasResponse {
  data?: CanvasCloudSnapshot | null
  updatedAtMs?: number
  error?: string
  code?: string
}

export const canvasStorageKeys = {
  tree: (module: string) => `mindCanvas.v2.tree.${module}`,
  links: (module: string) => `mindCanvas.v2.links.${module}`,
  hidden: (module: string) => `mindCanvas.v2.hidden.${module}`,
  mode: (module: string) => `mindCanvas.v2.mode.${module}`,
  savedAt: (module: string) => `mindCanvas.v2.savedAt.${module}`,
  serverAt: (module: string) => `mindCanvas.v2.serverAt.${module}`,
  treeHash: (module: string) => `mindCanvas.v2.treeHash.${module}`,
  pending: (module: string) => `mindCanvas.v2.pending.${module}`,
  hierarchyRevision: (module: string) => `mindCanvas.v2.hierarchyRevision.${module}`,
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? fallback : JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* storage unavailable/full */ }
}

function readPending(module: string): PendingCanvasChange | null {
  return readJson<PendingCanvasChange | null>(canvasStorageKeys.pending(module), null)
}

export function hasPendingCanvasSnapshot(module: string): boolean {
  return Boolean(readPending(module))
}

export function markCanvasSnapshotPending(module: string): string {
  const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  writeJson(canvasStorageKeys.pending(module), { id, changedAt: Date.now() } satisfies PendingCanvasChange)
  return id
}

export function readCanvasSnapshot(module: string): CanvasCloudSnapshot | null {
  const tree = readJson<MindTreeNode[]>(canvasStorageKeys.tree(module), [])
  if (!tree.length) return null
  return {
    v: 2,
    savedAt: Date.now(),
    tree,
    links: readJson<MindLink[]>(canvasStorageKeys.links(module), []),
    hidden: readJson<string[]>(canvasStorageKeys.hidden(module), []),
    mode: readJson<MindLayoutMode>(canvasStorageKeys.mode(module), 'right'),
    hierarchyRevision: readJson<string | undefined>(canvasStorageKeys.hierarchyRevision(module), undefined),
  }
}

function persistCloudSnapshot(module: string, snapshot: CanvasCloudSnapshot, serverAt: number) {
  writeJson(canvasStorageKeys.tree(module), snapshot.tree)
  writeJson(canvasStorageKeys.links(module), snapshot.links || [])
  writeJson(canvasStorageKeys.hidden(module), snapshot.hidden || [])
  writeJson(canvasStorageKeys.mode(module), snapshot.mode || 'right')
  writeJson(canvasStorageKeys.savedAt(module), serverAt)
  writeJson(canvasStorageKeys.serverAt(module), serverAt)
  writeJson(canvasStorageKeys.treeHash(module), JSON.stringify(snapshot.tree))
  if (snapshot.hierarchyRevision) writeJson(canvasStorageKeys.hierarchyRevision(module), snapshot.hierarchyRevision)
}

function notifySnapshotApplied(module: string, snapshot: CanvasCloudSnapshot, serverAt: number) {
  window.dispatchEvent(new CustomEvent('study-tool:canvas-snapshot-applied', {
    detail: { module, snapshot, serverAt },
  }))
}

function notifySnapshotSynced(module: string, serverAt: number) {
  window.dispatchEvent(new CustomEvent('study-tool:canvas-snapshot-synced', {
    detail: { module, serverAt },
  }))
}

async function putSnapshot(
  workspaceId: string,
  module: string,
  snapshot: CanvasCloudSnapshot,
  baseUpdatedAtMs: number,
  keepalive: boolean,
) {
  return remoteApiRequest(
    `canvas?workspaceId=${encodeURIComponent(workspaceId)}&module=${encodeURIComponent(module)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ data: { ...snapshot,
        hierarchyRevision: snapshot.hierarchyRevision || readJson<string | undefined>(canvasStorageKeys.hierarchyRevision(module), undefined),
      }, baseUpdatedAtMs }),
      ...(keepalive ? { keepalive: true } : {}),
    },
  )
}

export interface SyncCanvasModuleOptions {
  snapshot?: CanvasCloudSnapshot | null
  pullRemote?: boolean
  keepalive?: boolean
}

export type CanvasSyncResult = 'uploaded' | 'downloaded' | 'clean' | 'offline' | 'unauthenticated'

/**
 * Synchronize one canvas snapshot.
 *
 * A persisted pending marker is created only by a real user/local mutation. It
 * replaces the old tree-hash inference, which treated any stale browser cache
 * as an unsynced edit and therefore refused newer cloud hierarchy forever.
 */
export async function syncCanvasModule(
  module: string,
  options: SyncCanvasModuleOptions = {},
): Promise<CanvasSyncResult> {
  const identity = await getActiveIdentity()
  if (!identity.authenticated) return 'unauthenticated'
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'offline'

  const pending = readPending(module)
  const localSnapshot = options.snapshot ?? readCanvasSnapshot(module)
  if (pending && localSnapshot) {
    const snapshot: CanvasCloudSnapshot = {
      ...localSnapshot,
      v: 2,
      savedAt: pending.changedAt,
      writerDeviceId: identity.deviceId,
    }
    let baseUpdatedAtMs = readJson<number>(canvasStorageKeys.serverAt(module), 0)
    let response = await putSnapshot(identity.workspaceId, module, snapshot, baseUpdatedAtMs, Boolean(options.keepalive))

    // Another device committed after this device last pulled. Stale devices no
    // longer upload at all (no pending marker); a genuine current-session edit
    // retries against the returned revision, giving deterministic LWW.
    // 手动保存的画布编辑必须落盘：版本再被并发推进就带最新 revision 重推，
    // 最多三轮；仍失败留给下一轮同步重试。
    for (let retry = 0; response.status === 409 && retry < 2; retry += 1) {
      const conflict = await response.json().catch(() => ({})) as CanvasResponse
      if (conflict.code === 'hierarchy_repaired' && conflict.data) {
        // A restored cloud hierarchy must not be undone by an old cached auto-
        // reconciliation. Keep the entire pending tree for recovery, not notes.
        writeJson(`mindCanvas.v2.recovery.${identity.workspaceId}.${module}.${pending.id}`, { snapshot, pending })
        if (readPending(module)?.id !== pending.id) throw new Error('画布已修复，请稍后同步最新本地编辑')
        localStorage.removeItem(canvasStorageKeys.pending(module))
        const restoredAt = Number(conflict.updatedAtMs) || 0
        persistCloudSnapshot(module, conflict.data, restoredAt)
        notifySnapshotApplied(module, conflict.data, restoredAt)
        notifySnapshotSynced(module, restoredAt)
        return 'downloaded'
      }
      baseUpdatedAtMs = Number(conflict.updatedAtMs) || 0
      writeJson(canvasStorageKeys.serverAt(module), baseUpdatedAtMs)
      response = await putSnapshot(identity.workspaceId, module, snapshot, baseUpdatedAtMs, Boolean(options.keepalive))
    }
    if (!response.ok) throw new Error((await response.json().catch(() => ({})) as CanvasResponse).error || `画布同步失败（HTTP ${response.status}）`)

    const body = await response.json().catch(() => ({})) as CanvasResponse
    const serverAt = Number(body.updatedAtMs) || Date.now()
    // A newer local mutation may have happened while this upload was in flight.
    const latestPending = readPending(module)
    writeJson(canvasStorageKeys.savedAt(module), serverAt)
    writeJson(canvasStorageKeys.serverAt(module), serverAt)
    if (latestPending?.id === pending.id) {
      persistCloudSnapshot(module, snapshot, serverAt)
      localStorage.removeItem(canvasStorageKeys.pending(module))
      notifySnapshotSynced(module, serverAt)
    }
    return 'uploaded'
  }

  if (!options.pullRemote) return 'clean'
  const response = await remoteApiRequest(
    `canvas?workspaceId=${encodeURIComponent(identity.workspaceId)}&module=${encodeURIComponent(module)}`,
  )
  if (!response.ok) throw new Error(`画布读取失败（HTTP ${response.status}）`)
  const body = await response.json() as CanvasResponse
  const remoteAt = Number(body.updatedAtMs) || 0
  if (!body.data || !remoteAt) return 'clean'

  // Do not replace a user edit that appeared while the GET was in flight.
  if (readPending(module)) return 'clean'
  persistCloudSnapshot(module, body.data, remoteAt)
  notifySnapshotApplied(module, body.data, remoteAt)
  return 'downloaded'
}

function knownCanvasModules(): string[] {
  const modules = new Set<string>(CANVAS_MODULES)
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index) || ''
      for (const prefix of ['mindCanvas.v2.tree.', 'mindCanvas.v2.pending.']) {
        if (key.startsWith(prefix)) modules.add(key.slice(prefix.length))
      }
    }
  } catch { /* ignore */ }
  return [...modules]
}

/** Flush all locally pending canvases; explicit sync may also pull every module. */
export async function syncCanvasSnapshots(options: { pullRemote?: boolean } = {}) {
  const results = new Map<string, CanvasSyncResult>()
  const modules = knownCanvasModules().filter(module => options.pullRemote || hasPendingCanvasSnapshot(module))
  const resolved = await Promise.all(modules.map(async module => (
    [module, await syncCanvasModule(module, { pullRemote: options.pullRemote })] as const
  )))
  for (const [module, result] of resolved) results.set(module, result)
  return results
}
